import { useEffect, useState, type ReactNode } from 'react';

import { LanguageContext } from './language-context';
import {
  detectBrowserLocale,
  dictionaries,
  isLocale,
  LANGUAGE_QUERY_PARAMETER,
  LANGUAGE_STORAGE_KEY,
  type Locale,
} from './translations';

/** A language the address names wins over a saved choice and the browser's. */
const linkedLocale = (): Locale | null => {
  const linked = new URLSearchParams(window.location.search).get(LANGUAGE_QUERY_PARAMETER);
  return isLocale(linked) ? linked : null;
};

const initialLocale = (): Locale => {
  const linked = linkedLocale();
  if (linked !== null) return linked;
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // Browser detection still works when storage is unavailable.
  }
  return detectBrowserLocale(
    navigator.languages.length ? navigator.languages : [navigator.language],
  );
};

export const LanguageProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [locale, setLocale] = useState<Locale>(initialLocale);

  // Saved below like any choice, the linked language leaves the address, so a
  // later choice survives a reload and a shared address carries none.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(LANGUAGE_QUERY_PARAMETER)) return;
    url.searchParams.delete(LANGUAGE_QUERY_PARAMETER);
    window.history.replaceState(window.history.state, '', url);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
    } catch {
      // The current visit can still use the selected language.
    }
  }, [locale]);

  return (
    <LanguageContext.Provider value={{ locale, t: dictionaries[locale], setLocale }}>
      {children}
    </LanguageContext.Provider>
  );
};
